/**
 * Owner: Masa Bayu
 * File: dropcap-from-tip-face-object.tsx
 * Date:Time: 2026-10-14 04:03
 * Description: R10.3 Face thin shell · DropCapPaintPlan → DOM · NOT wired yet.
 */

import type { CSSProperties, ReactElement } from 'react';
import type { DropCapPaintPlan } from '@qx/qx-render';
import { FACE_BODY_INK, faceStackZIndex } from './qx-editor-page-face-objects';
import type { AssetLibrary, TextElement } from '../extract/spsu-types';
import { resolveCssFontFamily } from '../fonts/spsu-fonts';

type Props = {
  readonly plan: DropCapPaintPlan;
  /** Tip letter + flow plates · fonts / weight only */
  readonly tipElements: ReadonlyMap<string, TextElement>;
  readonly assets: AssetLibrary;
};

/**
 * Maps L4 DropCapPaintPlan to Face DOM.
 * No detect · no faceJustifyInstructions · no remasure.
 * Wire into renderBodyLayer = R10.4 only.
 */
export function DropCapFromTipFaceObject({
  plan,
  tipElements,
  assets,
}: Props): ReactElement {
  const letterTip = tipElements.get(plan.letter.elementId);
  const hostTip = tipElements.get(plan.dataAttrs['data-host-id']);
  const unit = letterTip?.bbox.unit ?? hostTip?.bbox.unit ?? 'pt';

  const hostFamily = resolveCssFontFamily(
    hostTip?.style.fontFamily ?? plan.flowLines[0]?.fontFamily ?? 'serif',
    assets,
  );
  const letterFamily = resolveCssFontFamily(
    letterTip?.style.fontFamily ?? plan.letter.style.fontFamily,
    assets,
  );

  const z =
    letterTip && hostTip
      ? Math.max(faceStackZIndex(letterTip), faceStackZIndex(hostTip))
      : 1;

  const box: CSSProperties = {
    position: 'absolute',
    left: `${plan.box.left}${unit}`,
    top: `${plan.box.top}${unit}`,
    width: `${plan.box.width}${unit}`,
    zIndex: z,
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    overflow: 'visible',
    fontFamily: hostFamily,
    color: FACE_BODY_INK,
  };

  // Children are box-relative (box = page columnLeft/top).
  const letterLeftPt = letterTip
    ? letterTip.bbox.x - plan.box.left
    : 0;
  const letterTopPt = letterTip ? letterTip.bbox.y - plan.box.top : 0;

  const letterStyle: CSSProperties = {
    position: 'absolute',
    left: `${letterLeftPt}${unit}`,
    top: `${letterTopPt}${unit}`,
    width: plan.letter.style.width,
    height: plan.letter.style.height,
    fontFamily: letterFamily,
    fontSize: plan.letter.style.fontSize,
    fontWeight:
      letterTip?.style.fontWeight === 'normal'
        ? 700
        : (letterTip?.style.fontWeight ?? 700),
    fontStyle: letterTip?.style.fontStyle,
    lineHeight: 0.9,
    margin: 0,
    padding: 0,
    whiteSpace: 'pre',
    color: FACE_BODY_INK,
    zIndex: 2,
    pointerEvents: 'none',
  };

  return (
    <div
      className="qx-dropcap-from-tip"
      style={box}
      data-native-dropcap={plan.dataAttrs['data-native-dropcap']}
      data-render-pipe={plan.dataAttrs['data-render-pipe']}
      data-letter-id={plan.dataAttrs['data-letter-id']}
      data-host-id={plan.dataAttrs['data-host-id']}
    >
      <div
        className="qx-dropcap-letter"
        data-element-id={plan.letter.elementId}
        style={letterStyle}
      >
        {plan.letter.content.trim()}
      </div>
      {plan.flowLines.map((line) => {
        const tip = tipElements.get(line.elementIds[0] ?? '');
        const lineStyle: CSSProperties = {
          position: 'absolute',
          left: `${line.marginLeft}${unit}`,
          top: `${line.top - plan.box.top}${unit}`,
          width: `${line.width}${unit}`,
          height: `${line.height}${unit}`,
          margin: 0,
          padding: 0,
          boxSizing: 'border-box',
          fontFamily: resolveCssFontFamily(line.fontFamily, assets),
          fontSize: `${line.fontSize}${unit}`,
          fontWeight: tip?.style.fontWeight,
          fontStyle: tip?.style.fontStyle,
          textAlign: line.textAlign,
          textAlignLast: line.textAlignLast,
          whiteSpace: 'pre',
          color: FACE_BODY_INK,
          zIndex: 1,
        };
        return (
          <div
            key={line.elementIds.join('-')}
            className="qx-dropcap-flow-line"
            data-element-id={line.elementIds[0]}
            data-text-align={line.dataTextAlign}
            style={lineStyle}
          >
            {line.content}
          </div>
        );
      })}
    </div>
  );
}
